import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { ArrowUpRight, SquarePen, Tag } from 'lucide-react';

import { NamedLink } from '../../../../components';
import MarketplaceCard from '../../../../components/MarketplaceCard/MarketplaceCard';
import useRevealOnScroll from '../../../../hooks/useRevealOnScroll';
import shouldReduceMotion from '../../../../util/shouldReduceMotion';
import css from './ResaleFocusSection.module.css';
import buttonCss from '../HomepageButtons.module.css';
import { fetchResaleListings, getResaleListings } from '../ResaleSection/ResaleSection.duck';
import { fetchReviews, getReviews } from '../../../ReviewsPage/ReviewsPage.duck';

const HERO_EYEBROW = 'Contemporary & Luxury Resale · NYC';
const HERO_TITLE_PLAIN = 'Their loss, ';
const HERO_TITLE_ACCENT = 'your wardrobe.';
const HERO_FULL_TITLE = `${HERO_TITLE_PLAIN}${HERO_TITLE_ACCENT}`;
const HERO_SUBTITLE =
  "Curated resale sourced directly from professional women's closets. Honest descriptions. 80% back to the seller.";

const RESALE_LABEL = 'Latest Arrivals';
const RESALE_TITLE = 'Shop now';
const RESALE_SUBTITLE = 'Buy the bag. Sell the dress. Keep 80%. Repeat.';

const ARCHIVE_LABEL = 'Community Reviews';
const ARCHIVE_TITLE = 'The Research Archive';
const ARCHIVE_SUBTITLE =
  'Not for sale — just research. 800+ honest reviews of contemporary brands, so you can shop smarter wherever you buy.';
const ARCHIVE_ROW_LIMIT = 10;

const REVEAL_DELAY_STEP_MS = 200;

const useStaggeredRowReveal = (isRowVisible, itemCount) => {
  const [isAnimated, setIsAnimated] = useState(() => shouldReduceMotion());

  useEffect(() => {
    if (itemCount === 0) {
      setIsAnimated(false);
      return undefined;
    }

    if (shouldReduceMotion()) {
      setIsAnimated(true);
      return undefined;
    }

    if (!isRowVisible || isAnimated || typeof window === 'undefined') {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      setIsAnimated(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isAnimated, isRowVisible, itemCount]);

  return isAnimated;
};

const ResaleFocusSectionComponent = ({
  listings,
  fetchListings,
  reviews,
  fetchReviewsData,
  isAuthenticated,
}) => {
  const [typedLength, setTypedLength] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const { ref: resaleRowRef, isVisible: isResaleRowVisible } = useRevealOnScroll();

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  useEffect(() => {
    fetchReviewsData();
  }, [fetchReviewsData]);

  useEffect(() => {
    if (shouldReduceMotion()) {
      setTypedLength(HERO_FULL_TITLE.length);
      setShowCursor(false);
      return undefined;
    }

    let startTimer;
    let tickTimer;
    let cursorTimer;
    let index = 0;

    const typeNextCharacter = () => {
      index += 1;
      setTypedLength(index);

      if (index < HERO_FULL_TITLE.length) {
        const delay = index <= HERO_TITLE_PLAIN.length ? 55 : 70;
        tickTimer = window.setTimeout(typeNextCharacter, delay);
      } else {
        cursorTimer = window.setTimeout(() => {
          setShowCursor(false);
        }, 2000);
      }
    };

    startTimer = window.setTimeout(typeNextCharacter, 600);

    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(tickTimer);
      window.clearTimeout(cursorTimer);
    };
  }, []);

  const typedTitleParts = useMemo(() => {
    const clampedLength = Math.max(0, Math.min(typedLength, HERO_FULL_TITLE.length));
    return {
      plain: HERO_FULL_TITLE.slice(0, Math.min(clampedLength, HERO_TITLE_PLAIN.length)),
      accent:
        clampedLength > HERO_TITLE_PLAIN.length
          ? HERO_FULL_TITLE.slice(HERO_TITLE_PLAIN.length, clampedLength)
          : '',
    };
  }, [typedLength]);

  const resaleListings = Array.isArray(listings) ? listings.slice(0, 10) : [];
  const reviewListings = Array.isArray(reviews) ? reviews.slice(0, ARCHIVE_ROW_LIMIT) : [];
  const isResaleRowAnimated = useStaggeredRowReveal(isResaleRowVisible, resaleListings.length);

  return (
    <section className={css.root}>
      <div className={css.outer}>
        <section className={css.heroSection}>
          <p className={css.heroEyebrow}>{HERO_EYEBROW}</p>
          <h1 className={css.heroTitle} aria-label={HERO_FULL_TITLE}>
            <span className={css.typedChunk}>{typedTitleParts.plain}</span>
            {typedTitleParts.accent ? (
              <span className={css.heroTitleAccent}>{typedTitleParts.accent}</span>
            ) : null}
            {showCursor ? <span aria-hidden="true" className={css.typedCursor} /> : null}
          </h1>
          <p className={css.heroSubtitle}>{HERO_SUBTITLE}</p>
          <div className={css.heroActions}>
            <NamedLink
              name="ResalePage"
              className={`${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark}`}
            >
              <ArrowUpRight aria-hidden="true" className={buttonCss.icon} strokeWidth={2} />
              <span>SHOP NOW</span>
            </NamedLink>
            <NamedLink
              name="StartSellingSignupPage"
              className={`${buttonCss.button} ${buttonCss.buttonSecondary} ${buttonCss.buttonLight}`}
            >
              <Tag aria-hidden="true" className={buttonCss.icon} strokeWidth={2} />
              <span>START SELLING</span>
            </NamedLink>
          </div>
        </section>
      </div>

      <div className={css.sectionDivider} />

      <section className={css.sectionBlock}>
        <div className={css.outer}>
          <div className={css.sectionHead}>
            <div>
              <p className={css.sectionLabel}>{RESALE_LABEL}</p>
              <h2 className={css.sectionTitle}>{RESALE_TITLE}</h2>
              <p className={css.sectionSubtitle}>{RESALE_SUBTITLE}</p>
            </div>
            <NamedLink name="ResalePage" className={css.viewAll}>
              View all
            </NamedLink>
          </div>

          <div className={css.horizontalRow} ref={resaleRowRef}>
            {resaleListings.length > 0 ? (
              resaleListings.map((listing, index) => (
                <div
                  key={`${listing?.id?.uuid || 'resale'}-${index}`}
                  className={
                    isResaleRowAnimated
                      ? `${css.revealCard} ${css.revealCardVisible}`
                      : css.revealCard
                  }
                  style={{ '--reveal-delay': `${index * REVEAL_DELAY_STEP_MS}ms` }}
                >
                  <MarketplaceCard listing={listing} variant="resale" className={css.rowCard} />
                </div>
              ))
            ) : (
              <div className={css.emptyState}>Resale listings are loading…</div>
            )}
          </div>
        </div>
      </section>

      <section className={css.archiveBlock}>
        <div className={css.outer}>
          <div className={css.sectionHead}>
            <div>
              <p className={css.sectionLabel}>{ARCHIVE_LABEL}</p>
              <h2 className={css.sectionTitle}>{ARCHIVE_TITLE}</h2>
              <p className={css.sectionSubtitle}>{ARCHIVE_SUBTITLE}</p>
            </div>
            <NamedLink name="ReviewsPage" className={css.viewAll}>
              View all
            </NamedLink>
          </div>

          <div className={css.reviewHorizontalRow}>
            {reviewListings.length > 0 ? (
              reviewListings.map(listing => (
                <MarketplaceCard
                  key={listing?.id?.uuid || listing?.id || 'review-card'}
                  listing={listing}
                  variant="review"
                  className={`${css.reviewScrollCard} ${css.reviewRowCard}`}
                />
              ))
            ) : (
              <div className={css.emptyState}>Review cards are loading…</div>
            )}
          </div>

          <div className={css.archiveCta}>
            <div className={css.archiveCtaText}>
              <strong>Recently bought something?</strong>
              <span>No five-star pressure. Just be honest.</span>
            </div>
            <NamedLink
              name={isAuthenticated ? 'AddMyReviewPage' : 'SignupPage'}
              className={`${buttonCss.button} ${buttonCss.buttonPrimary} ${buttonCss.buttonDark} ${css.archiveCtaButton}`}
            >
              <SquarePen
                aria-hidden="true"
                className={`${buttonCss.icon} ${css.archiveCtaButtonIcon}`}
                strokeWidth={2}
              />
              <span>ADD A REVIEW</span>
            </NamedLink>
          </div>
        </div>
      </section>
    </section>
  );
};

const mapStateToProps = state => ({
  listings: getResaleListings(state),
  reviews: getReviews(state),
  isAuthenticated: state.auth?.isAuthenticated,
});

const mapDispatchToProps = dispatch => ({
  fetchListings: () => dispatch(fetchResaleListings()),
  fetchReviewsData: () => dispatch(fetchReviews({ perPage: 12 })),
});

const ResaleFocusSection = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ResaleFocusSectionComponent);

export default ResaleFocusSection;
